import { useState, useEffect, useCallback, useRef } from 'react';
import { fonts as fontsApi, tags as tagsApi, collections as collectionsApi, activations as activationsApi, stats as statsApi } from '../api/client.js';

// ─── useFonts ──────────────────────────────────────────

export function useFonts(params = {}) {
  const [data, setData] = useState({ fonts: [], pagination: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetch = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError(null);
    try {
      // Filter out empty/undefined params
      const merged = { ...paramsRef.current, ...overrides };
      const cleanParams = Object.fromEntries(
        Object.entries(merged).filter(([_, v]) => v !== undefined && v !== null && v !== '')
      );
      const result = await fontsApi.list(cleanParams);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refetch when params change
  useEffect(() => {
    fetch();
  }, [
    params.search,
    params.category,
    params.collection,
    params.activated,
    params.favorite,
    params.tag,
    params.startsWith,
    params.page,
    params.limit,
    params.sort,
    params.order,
    fetch,
  ]);

  const toggleFavorite = useCallback(async (fontId) => {
    try {
      const result = await fontsApi.toggleFavorite(fontId);
      // Update local state immediately
      setData(prev => ({
        ...prev,
        fonts: prev.fonts.map(f =>
          f.id === fontId ? { ...f, is_favorited: result.favorited } : f
        ),
      }));
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const toggleActivation = useCallback(async (fontId, active) => {
    try {
      const result = await activationsApi.toggle(fontId, active);
      // Update local state immediately
      setData(prev => ({
        ...prev,
        fonts: prev.fonts.map(f =>
          f.id === fontId ? { ...f, is_activated: result.is_active } : f
        ),
      }));
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const deleteFont = useCallback(async (fontId) => {
    try {
      await fontsApi.delete(fontId);
      setData(prev => ({
        ...prev,
        fonts: prev.fonts.filter(f => f.id !== fontId),
        pagination: { ...prev.pagination, total: prev.pagination.total - 1 },
      }));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const rateFont = useCallback(async (fontId, rating) => {
    try {
      const result = await fontsApi.rate(fontId, rating);
      // Optimistic local update
      setData(prev => ({
        ...prev,
        fonts: prev.fonts.map(f =>
          f.id === fontId ? { ...f, avg_rating: result.avg_rating, rating_count: result.rating_count, user_rating: result.user_rating } : f
        ),
      }));
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  return {
    fonts: data.fonts,
    pagination: data.pagination,
    loading,
    error,
    refetch: fetch,
    toggleFavorite,
    toggleActivation,
    deleteFont,
    rateFont,
  };
}

// ─── useFontDetail ─────────────────────────────────────

export function useFontDetail(fontId) {
  const [font, setFont] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fontIdRef = useRef(fontId);
  fontIdRef.current = fontId;

  const fetchDetail = useCallback(async (id) => {
    const targetId = id || fontIdRef.current;
    if (!targetId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fontsApi.get(targetId);
      // Only set if still looking at same font
      if (fontIdRef.current === targetId) setFont(data);
    } catch (err) {
      if (fontIdRef.current === targetId) setError(err.message);
    } finally {
      if (fontIdRef.current === targetId) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fontId) {
      setFont(null);
      return;
    }
    fetchDetail(fontId);
  }, [fontId, fetchDetail]);

  return { font, loading, error, refetch: fetchDetail };
}

// ─── useFontUpload ─────────────────────────────────────

export function useFontUpload() {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);

  const upload = useCallback(async (files, collectionId, licenseFiles, options = {}) => {
    setUploading(true);
    setError(null);
    setProgress({ current: 0, total: files.length });
    try {
      const result = await fontsApi.upload(files, collectionId, licenseFiles, options);
      setProgress({ current: files.length, total: files.length });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setUploading(false);
    }
  }, []);

  return { upload, uploading, progress, error };
}

// ─── useCollections ────────────────────────────────────

export function useCollections() {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const result = await collectionsApi.list();
      setCollections(result.collections);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const create = useCallback(async (data) => {
    const col = await collectionsApi.create(data);
    setCollections(prev => [...prev, { ...col, font_count: 0 }]);
    return col;
  }, []);

  const update = useCallback(async (id, data) => {
    const col = await collectionsApi.update(id, data);
    setCollections(prev => prev.map(c => c.id === id ? { ...c, ...col } : c));
    return col;
  }, []);

  const remove = useCallback(async (id) => {
    await collectionsApi.delete(id);
    setCollections(prev => prev.filter(c => c.id !== id));
  }, []);

  const addFonts = useCallback(async (collectionId, fontIds) => {
    const result = await collectionsApi.addFonts(collectionId, fontIds);
    await fetch(); // refresh counts
    return result;
  }, [fetch]);

  const removeFont = useCallback(async (collectionId, fontId) => {
    await collectionsApi.removeFont(collectionId, fontId);
    await fetch(); // refresh counts
  }, [fetch]);

  return {
    collections,
    loading,
    error,
    refetch: fetch,
    create,
    update,
    remove,
    addFonts,
    removeFont,
  };
}

// ─── useTags ──────────────────────────────────────────

export function useTags() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const result = await tagsApi.list();
      setTags(result.tags);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const create = useCallback(async (data) => {
    const tag = await tagsApi.create(data);
    setTags(prev => [...prev, { ...tag, font_count: 0 }]);
    return tag;
  }, []);

  const remove = useCallback(async (id) => {
    await tagsApi.delete(id);
    setTags(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToFont = useCallback(async (fontId, tagIds) => {
    const result = await fontsApi.addTags(fontId, tagIds);
    return result;
  }, []);

  const removeFromFont = useCallback(async (fontId, tagId) => {
    await fontsApi.removeTag(fontId, tagId);
  }, []);

  return {
    tags,
    loading,
    error,
    refetch: fetch,
    create,
    remove,
    addToFont,
    removeFromFont,
  };
}

// ─── useStats ──────────────────────────────────────────

export function useStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    statsApi.dashboard().then(setData).catch(console.error);
  }, []);

  useEffect(() => {
    statsApi.dashboard()
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const setActiveCount = useCallback((count) => {
    setData(prev => prev ? {
      ...prev,
      my_activations: { ...prev.my_activations, count }
    } : prev);
  }, []);

  return { stats: data, loading, setActiveCount, refresh };
}
